import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PaymentElement } from '@stripe/react-stripe-js';
import {
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { upiPaymentMethodSelected } from 'utils/eventing/upiPaymentMethodSelected';
import { StripeFormProps } from './types';

const PAYMENT_ELEMENT_OPTIONS = {
  fields: {
    billingDetails: {
      address: 'if_required',
    },
  },
} as const satisfies StripePaymentElementOptions;

// UPI-only overlay: name/country/state are already collected by the custom
// form, so hide them in the Element. line1/line2/city/postalCode stay auto
// since the India short form does not collect them.
const UPI_FIELDS = {
  billingDetails: {
    name: 'never',
    address: {
      country: 'never',
      state: 'never',
      line1: 'auto',
      line2: 'auto',
      city: 'auto',
      postalCode: 'auto',
    },
  },
} as const satisfies StripePaymentElementOptions['fields'];

export const StripeForm = ({ isDisabled }: StripeFormProps) => {
  const [selectedMethodType, setSelectedMethodType] = useState<string | null>(
    null,
  );
  const previousMethodType = useRef<string | null>(null);

  const options = useMemo<StripePaymentElementOptions>(
    () => ({
      fields:
        selectedMethodType === 'upi'
          ? UPI_FIELDS
          : PAYMENT_ELEMENT_OPTIONS.fields,
      readOnly: isDisabled,
    }),
    [selectedMethodType, isDisabled],
  );

  const handleChange = (event: StripePaymentElementChangeEvent) => {
    const nextMethodType = event.value.type;
    if (previousMethodType.current !== 'upi' && nextMethodType === 'upi') {
      upiPaymentMethodSelected();
    }
    previousMethodType.current = nextMethodType;
    setSelectedMethodType(nextMethodType);
  };

  return (
    <fieldset>
      <legend className="mb-0">
        <h2 className="h5">
          <FormattedMessage
            id="payment.billing.information.form.heading"
            defaultMessage="Billing Information (Required)"
            description="Heading for Billing Form"
          />
        </h2>
      </legend>
      <PaymentElement
        options={options}
        id="payment-element"
        onChange={handleChange}
      />
    </fieldset>
  );
};
